<template>
  <div class="timefield" ref="root">
    <button
      type="button"
      :class="['input', 'sel-ctrl', { focus: open }]"
      @click="open = !open"
    >
      <span class="tnum">{{ value }}</span>
      <WhIcon name="clock" size="16" />
    </button>
    <div v-if="open" ref="pop" class="time-pop">
      <div
        v-for="tm in times"
        :key="tm"
        :class="['time-opt', { on: tm === value }]"
        @click="pick(tm)"
      >
        {{ tm }}
      </div>
    </div>
  </div>
</template>

<script>
import WhIcon from "./WhIcon.vue";

// Custom time dropdown that replaces the browser's native <input type="time">
// popover (which can't be styled to match the editor). Lists times at a fixed
// step and opens scrolled near the current value.
export default {
  name: "WhTimeField",
  components: { WhIcon },
  props: {
    value: { type: String, default: "09:00" },
    step: { type: Number, default: 30 }, // minutes between options
  },
  data() {
    return { open: false };
  },
  computed: {
    times() {
      const out = [];
      for (let m = 0; m < 24 * 60; m += this.step) {
        const h = String(Math.floor(m / 60)).padStart(2, "0");
        const min = String(m % 60).padStart(2, "0");
        out.push(`${h}:${min}`);
      }
      return out;
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        document.addEventListener("mousedown", this.onDocClick);
        this.$nextTick(this.scrollToValue);
      } else {
        document.removeEventListener("mousedown", this.onDocClick);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onDocClick);
  },
  methods: {
    pick(tm) {
      this.$emit("input", tm);
      this.open = false;
    },
    onDocClick(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target))
        this.open = false;
    },
    scrollToValue() {
      const pop = this.$refs.pop;
      const i = this.times.indexOf(this.value);
      // Bring the selected option near the top without scrollIntoView jank.
      if (pop && i >= 0) pop.scrollTop = Math.max(0, i * 38 - 76);
    },
  },
};
</script>

<style scoped>
.timefield {
  position: relative;
  flex: 1;
  min-width: 0;
}
.input {
  font-family: var(--wh-num);
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  width: 100%;
  border: 1px solid var(--line-strong);
  background: var(--surface);
  border-radius: var(--r-sm);
  padding: 9px 12px;
  outline: none;
  transition: 0.14s;
}
.input.sel-ctrl {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}
.input.sel-ctrl .wh-icon {
  color: var(--faint);
}
.input.sel-ctrl.focus {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-tint);
}
.tnum {
  font-family: var(--wh-num);
  font-variant-numeric: tabular-nums;
}
.time-pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 40;
  background: var(--surface);
  border: 1px solid var(--line-strong);
  border-radius: var(--r-sm);
  box-shadow: var(--shadow-pop);
  max-height: 212px;
  overflow-y: auto;
  padding: 5px;
  scrollbar-width: thin;
}
.time-pop::-webkit-scrollbar {
  width: 8px;
}
.time-pop::-webkit-scrollbar-thumb {
  background: var(--line-strong);
  border-radius: 8px;
  border: 2px solid var(--surface);
}
.time-opt {
  font-family: var(--wh-num);
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-2);
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: 0.1s;
}
.time-opt:hover {
  background: var(--surface-3);
  color: var(--ink);
}
.time-opt.on {
  background: var(--brand);
  color: #fff;
}
</style>
